import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, TextField, Button, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, Stack, Tooltip
} from "@mui/material";
import {
    Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, 
    Save as SaveIcon, Label as LabelIcon
} from "@mui/icons-material";
import {
    collection, getDocs, doc, setDoc, query, where, orderBy, deleteDoc, getDoc, addDoc
} from "firebase/firestore";
import { db, auth } from "../../../firebase";
import { toast } from "react-toastify";

function Discountypes() {
    const [schoolId, setSchoolId] = useState("");
    const [discountTypes, setDiscountTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        discountName: "",
        minAmount: "",
        maxAmount: "",
        sequence: ""
    });

    useEffect(() => {
        const uid = auth.currentUser?.uid;
        if (uid) fetchSchoolId(uid);
    }, []);

    useEffect(() => {
        if (schoolId) fetchData();
    }, [schoolId]);

    const fetchSchoolId = async (uid) => {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) setSchoolId(userDoc.data().schoolId);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, 'discountTypes'), 
                where('schoolId', '==', schoolId), 
                orderBy('sequence', 'asc')
            );
            const snap = await getDocs(q);
            setDiscountTypes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error(err);
            toast.error("Failed to load discount types");
        }
        setLoading(false);
    };

    const handleOpen = (data = null) => {
        if (data) {
            setIsEdit(true);
            setCurrentId(data.id);
            setFormData({
                discountName: data.discountName,
                minAmount: data.minAmount,
                maxAmount: data.maxAmount,
                sequence: data.sequence
            });
        } else {
            setIsEdit(false);
            setFormData({ discountName: "", minAmount: "", maxAmount: "", sequence: "" });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setIsEdit(false);
        setCurrentId(null);
    };

    const handleSave = async () => {
        const { discountName, minAmount, maxAmount, sequence } = formData;
        if (!discountName || !sequence) return toast.warning("Name and Sequence are required");

        setLoading(true);
        try {
            const payload = {
                schoolId,
                discountName,
                minAmount: Number(minAmount) || 0,
                maxAmount: Number(maxAmount) || 0,
                sequence: Number(sequence),
                updatedAt: new Date().toISOString()
            };

            if (isEdit) {
                await setDoc(doc(db, "discountTypes", currentId), payload, { merge: true });
                toast.success("Updated successfully");
            } else {
                await addDoc(collection(db, "discountTypes"), payload);
                toast.success("Added successfully");
            }
            handleClose();
            fetchData();
        } catch (err) {
            toast.error("Save failed");
        }
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this discount type?")) {
            try {
                await deleteDoc(doc(db, "discountTypes", id));
                toast.success("Deleted");
                fetchData();
            } catch (err) {
                toast.error("Delete failed");
            }
        }
    };

    return (
        <Box p={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h5" fontWeight="700">Discount Types</Typography>
                    <Typography variant="body2" color="text.secondary">Manage school-wide discount categories</Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
                    Add Discount Type
                </Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sr. No</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Discount Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Min Amount</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Max Amount</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sequence</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {discountTypes.map((item, index) => (
                            <TableRow key={item.id} hover>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <LabelIcon fontSize="small" color="action" />
                                        <Typography variant="body2" fontWeight="500">{item.discountName}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>₹{item.minAmount?.toLocaleString()}</TableCell>
                                <TableCell>₹{item.maxAmount?.toLocaleString()}</TableCell>
                                <TableCell>{item.sequence}</TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" color="primary" onClick={() => handleOpen(item)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                        {discountTypes.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                                    No discount types defined.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {isEdit ? "Update Discount Type" : "New Discount Type"}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} pt={1}>
                        <TextField
                            label="Discount Name "
                            fullWidth
                            size="small"
                            value={formData.discountName}
                            onChange={(e) => setFormData({ ...formData, discountName: e.target.value })}
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Min Amount"
                                type="number"
                                fullWidth
                                size="small"
                                value={formData.minAmount}
                                onChange={(e) => setFormData({ ...formData, minAmount: e.target.value })}
                            />
                            <TextField
                                label="Max Amount"
                                type="number"
                                fullWidth
                                size="small"
                                value={formData.maxAmount}
                                onChange={(e) => setFormData({ ...formData, maxAmount: e.target.value })}
                            />
                        </Stack>
                        <TextField
                            label="Sequence Order"
                            type="number"
                            fullWidth
                            size="small"
                            value={formData.sequence}
                            onChange={(e) => setFormData({ ...formData, sequence: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<SaveIcon />} 
                        onClick={handleSave} 
                        disabled={loading}
                    >
                        {isEdit ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Discountypes;